import React from 'react'
import { motion } from 'framer-motion'
import { Chrome, ArrowRight } from 'lucide-react'

interface HeroSectionProps {
  companyLogos: Array<{
    name: string
    logo: string
    width: number
  }>
}

const HeroSection: React.FC<HeroSectionProps> = ({ companyLogos }) => {
  return (
    <section id="hero" className="relative min-h-[90vh] sm:min-h-screen bg-white overflow-hidden">
      {/* Subtle, professional background */}
      <div className="absolute inset-0">
        <div className="absolute top-8 right-4 sm:top-20 sm:right-20 w-32 h-32 sm:w-72 sm:h-72 bg-blue-50/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-8 left-4 sm:bottom-20 sm:left-20 w-40 h-40 sm:w-96 sm:h-96 bg-gray-50/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-[90vh] sm:min-h-screen max-w-7xl mx-auto px-4 sm:px-6 text-center w-full">
        <div className="max-w-4xl mx-auto pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-12 md:pb-16">
          {/* Clean, professional headline */}
          <motion.div
            className="mb-6 sm:mb-8 md:mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight">
              Stop losing tabs{' '}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Start organizing
              </span>
            </h1>
          </motion.div>

          {/* Clean subtitle */}
          <motion.div
            className="mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
          >
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              No more{' '}
              <span className="bg-gray-100 text-gray-700 px-2 py-1 sm:px-3 sm:py-1 rounded-md font-mono text-sm sm:text-base">
                Ctrl+Shift+T
              </span>
              . Save your entire browser session with{' '}
              <span className="bg-yellow-100 text-yellow-700 px-2 py-1 sm:px-3 sm:py-1 rounded-md font-medium">
                one click
              </span>
              . Access from anywhere.
            </p>
          </motion.div>

          {/* Professional CTA - more compelling */}
          <motion.div
            className="mb-8 sm:mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            <a 
              href="https://chrome.google.com/webstore/detail/mmoeppckalbggegohbocmbkpfmnocpcd"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 sm:px-10 sm:py-5 bg-gray-900 text-white rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Chrome className="w-5 h-5 sm:w-6 sm:h-6 mr-3" />
              <span>Add to Chrome - Free</span>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-3" />
            </a>
          </motion.div>

          {/* Clean stats section */}
          <motion.div
            className="mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
          >
            <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {[
                { value: '100K+', label: 'Active Users' },
                { value: '4.9★', label: 'Chrome Rating' },
                { value: '2M+', label: 'Sessions Saved' }
              ].map((stat, index) => (
                <div key={index} className="text-center flex flex-col items-center">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium leading-tight">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Professional company section - compact mobile design */}
          <motion.div
            className="mt-4 sm:mt-6 md:mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200 py-4 px-4 sm:py-5 sm:px-6">
              <p className="text-center text-base sm:text-lg text-gray-700 mb-4 font-medium">
                Trusted by professionals everywhere
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8">
                {companyLogos.map((company, index) => (
                  <div key={index} className="grayscale hover:grayscale-0 transition-all duration-300">
                    <img
                      src={company.logo}
                      alt={company.name}
                      style={{ width: Math.min(company.width * 0.8, 48), maxWidth: 48 }}
                      className="h-6 sm:h-8 md:h-9 object-contain opacity-70 hover:opacity-100"
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection 